import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from '../../hooks/useRepo';
import { useTranslation } from 'react-i18next';
import styles from './OfferList.module.scss';
import { T, A } from 'TOMA';
import {
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
} from '@mui/material';
import { DateRange } from '@mui/icons-material';
import s from '../Event/Event.module.scss';

export interface OfferAttributes {
  type: string;
  url: string;
}

export interface OfferRecord {
  attributes?: OfferAttributes;
  Id?: string;
  name: string;
  Description__c?: string | null;
  Discount_Amount__c?: number | null;
  Discount_Percentage__c?: number | null;
  Price_HT__c?: number | null;
  Price_TTC__c?: number | null;
  Original_Price__c?: number | null;
  Currency__c?: string;
  Size__c?: string;
  Subtitle__c?: string;
  Area__c?: string;
  offer_id?: string;
  price?: number;
  description?: string | null;
  inclusions?: string | null;
}

export interface ManualOffer {
  id: string;
  name: string;
  price: number;
  description: string;
  inclusions: string;
}

export interface OfferResponse {
  totalSize: number;
  done: boolean;
  records: OfferRecord[];
}

interface OfferListProps {
  eventId?: string;
  isModal?: boolean;
}

const emptyForm = {
  name: '',
  price: '',
  description: '',
  inclusions: '',
};

export default function OfferList({
  eventId: propEventId,
  isModal = false,
}: OfferListProps): JSX.Element {
  const { companyRepository, eventRepository } = useApiContext();
  const { pathname } = useLocation();
  const { eventId: paramEventId } = useParams();
  const eventId = propEventId ?? paramEventId;
  const { t } = useTranslation();

  const [manualOffers, setManualOffers] = useState<ManualOffer[]>([]);
  const [successForm, setSuccessForm] = useState<string | null>(null);
  const [errorForm, setErrorForm] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Popin state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const { response: eventData } = useRepo(async () => {
    return await eventRepository.getEventById(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      eventId!
    );
  });

  // Load existing offers for this event
  useEffect(() => {
    if (eventId) {
      setLoading(true);
      companyRepository
        .offerByEvent(eventId)
        .then((eventOffers) => {
          const loadedOffers: ManualOffer[] = eventOffers.map(
            (offer: OfferRecord, index: number) => {
              const rawPrice = offer.price ?? offer.Discount_Amount__c ?? 0;
              const numericPrice =
                typeof rawPrice === 'number'
                  ? rawPrice
                  : parseFloat(String(rawPrice));
              return {
                id: offer.offer_id ?? `offer-${Date.now()}-${index}`,
                name: offer.name,
                price: isNaN(numericPrice) ? 0 : numericPrice,
                description: offer.description ?? '',
                inclusions: offer.inclusions ?? '',
              };
            }
          );
          setManualOffers(loadedOffers);
        })
        .catch((error) => {
          console.error('Error loading offers:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [eventId, companyRepository]);

  // --- Popin handlers ---
  const openCreateDialog = (): void => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (offer: ManualOffer): void => {
    setEditingId(offer.id);
    setForm({
      name: offer.name,
      price: String(offer.price ?? ''),
      description: offer.description ?? '',
      inclusions: offer.inclusions ?? '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = (): void => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setDialogError(null);
  };

  const handleFormChange = (
    field: keyof typeof emptyForm,
    value: string
  ): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Persist the given offers list to the backend (full replace).
  // Called automatically after create / edit / remove — no manual save.
  const persistOffers = async (
    offersList: ManualOffer[],
    successMessage: string
  ): Promise<boolean> => {
    setLoading(true);
    const offersData = offersList.map((offer) => ({
      name: offer.name,
      price: offer.price,
      description: offer.description,
      inclusions: offer.inclusions,
    }));

    try {
      await companyRepository.offerStore(eventId ?? '', offersData as any);
      setSuccessForm(successMessage);
      setErrorForm(null);
      return true;
    } catch (error) {
      console.error('Error saving offers:', error);
      setErrorForm(t('offers:errors.save'));
      setSuccessForm(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDialogConfirm = async (): Promise<void> => {
    if (!form.name.trim()) {
      setDialogError(t('offers:errors.nameRequired'));
      return;
    }
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) {
      setDialogError(t('offers:errors.priceInvalid'));
      return;
    }

    let nextOffers: ManualOffer[];
    let successMsg: string;

    if (editingId) {
      // Update existing
      nextOffers = manualOffers.map((o) =>
        o.id === editingId
          ? {
              ...o,
              name: form.name.trim(),
              price: priceNum,
              description: form.description.trim(),
              inclusions: form.inclusions.trim(),
            }
          : o
      );
      successMsg = t('offers:successUpdate');
    } else {
      // Create new
      const newOffer: ManualOffer = {
        id: `offer-${Date.now()}`,
        name: form.name.trim(),
        price: priceNum,
        description: form.description.trim(),
        inclusions: form.inclusions.trim(),
      };
      nextOffers = [...manualOffers, newOffer];
      successMsg = t('offers:successCreate');
    }

    const ok = await persistOffers(nextOffers, successMsg);
    if (ok) {
      setManualOffers(nextOffers);
      closeDialog();
    }
  };

  // Remove offer — persists immediately
  const handleRemoveOffer = async (offerId: string): Promise<void> => {
    const nextOffers = manualOffers.filter((offer) => offer.id !== offerId);
    const ok = await persistOffers(nextOffers, t('offers:successDelete'));
    if (ok) {
      setManualOffers(nextOffers);
    }
  };

  const inclusionsToList = (raw: string): string[] =>
    raw
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

  return (
    <T.Container>
      <div className={styles.container}>
        {loading && (
          <div>
            <A.Alert alert="info" side="ℹ️">
              {t('offers:loading')}
            </A.Alert>
          </div>
        )}
        <T.Block
          style={{
            padding: '20px',
            borderRadius: '10px',
            backgroundColor: '#F2F2F2',
          }}
        >
          <T.BlockResponsive>
            {/* <A.Title className={s.eventInfoTitle} as="h2" fontSize="32">
              {t('event:event_information')}
            </A.Title>
            <A.Line mb="20" mt="10" />
            <T.Flex direction="column" gap={2}>
              <TextField
                sx={{ backgroundColor: '#F2F2F2' }}
                value={eventData?.name ?? ''}
                label={t('event:name_of_the_event')}
                variant="outlined"
                disabled
              />
              <TextField
                sx={{ backgroundColor: '#F2F2F2' }}
                value={
                  eventData?.date
                    ? new Date(eventData.date).toLocaleDateString('en-GB')
                    : ''
                }
                label={t('event:date_of_event')}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange />
                    </InputAdornment>
                  ),
                }}
                disabled
              />
              <TextField
                sx={{ backgroundColor: '#F2F2F2' }}
                value={eventData?.city ?? ''}
                label={t('event:city_of_event')}
                variant="outlined"
                disabled
              />
              <TextField
                sx={{ backgroundColor: '#F2F2F2' }}
                value={eventData?.country ?? ''}
                label={t('event:country_of_event')}
                variant="outlined"
                disabled
              />
              <TextField
                sx={{ backgroundColor: '#F2F2F2' }}
                value={
                  eventData?.deadline_date
                    ? new Date(eventData.deadline_date).toLocaleDateString(
                        'en-GB'
                      )
                    : ''
                }
                label={t('event:registration_closing_date')}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange />
                    </InputAdornment>
                  ),
                }}
                disabled
              />
            </T.Flex> */}

            <T.Flex halign="space-between" valign="center" mt="40" mb="10">
              <A.Title className={s.eventInfoTitle} as="h2" fontSize="32">
                {t('event:offers')}
              </A.Title>
              <button
                type="button"
                className={styles.addOfferBtn}
                onClick={openCreateDialog}
              >
                + {t('offers:addNewOffer')}
              </button>
            </T.Flex>
            <A.Line mb="20" mt="10" />

            {/* Offers list */}
            {manualOffers.length > 0 ? (
              <T.Block>
                {manualOffers.map((offer) => {
                  const inclusionItems = inclusionsToList(offer.inclusions);
                  return (
                    <div key={offer.id} className={styles.offerCard}>
                      <div className={styles.offerCardMain}>
                        <div className={styles.offerCardHeader}>
                          <div className={styles.offerCardName}>
                            {offer.name}
                          </div>
                          <div className={styles.offerCardPrice}>
                            {Number(offer.price ?? 0).toFixed(2)} €
                          </div>
                        </div>
                        {offer.description && (
                          <div className={styles.offerCardDescription}>
                            {offer.description}
                          </div>
                        )}
                        {inclusionItems.length > 0 && (
                          <ul className={styles.offerCardInclusions}>
                            {inclusionItems.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className={styles.offerCardActions}>
                        <button
                          type="button"
                          className={styles.offerCardBtn}
                          onClick={() => openEditDialog(offer)}
                        >
                          {t('offers:edit')}
                        </button>
                        <button
                          type="button"
                          className={`${styles.offerCardBtn} ${styles.danger}`}
                          onClick={() => {
                            void handleRemoveOffer(offer.id);
                          }}
                        >
                          {t('offers:remove')}
                        </button>
                      </div>
                    </div>
                  );
                })}

                <T.Block mt="20">
                  {successForm && (
                    <A.Alert alert="success" side="🎉" mb="15">
                      {successForm}
                    </A.Alert>
                  )}

                  {errorForm && (
                    <A.Alert alert="error" side="⚠️" mb="15">
                      <A.InnerHTML html={errorForm} />
                    </A.Alert>
                  )}
                </T.Block>
              </T.Block>
            ) : (
              !loading && (
                <div className={styles.emptyOffers}>{t('offers:noOffers')}</div>
              )
            )}
          </T.BlockResponsive>
        </T.Block>
      </div>

      {/* ===== Create / Edit Offer Popin ===== */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogContent className={styles.dialogContent}>
          <h3 className={styles.dialogTitle}>
            {editingId ? t('offers:editOffer') : t('offers:addNewOffer')}
          </h3>

          <div className={styles.dialogField}>
            <TextField
              fullWidth
              autoFocus
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              label={t('event:offer_name')}
              variant="outlined"
              placeholder={t('offers:enterOfferName')}
            />
          </div>

          <div className={styles.dialogField}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              label={t('offers:description')}
              variant="outlined"
              placeholder={t('offers:enterDescription')}
            />
          </div>

          <div className={styles.dialogField}>
            <TextField
              fullWidth
              value={form.price}
              onChange={(e) => handleFormChange('price', e.target.value)}
              label={t('event:price')}
              variant="outlined"
              type="number"
              placeholder="0.00"
            />
          </div>

          <div className={styles.dialogField}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={form.inclusions}
              onChange={(e) => handleFormChange('inclusions', e.target.value)}
              label={t('offers:inclusions')}
              variant="outlined"
              placeholder={t('offers:inclusionsPlaceholder')}
              helperText={t('offers:inclusionsHelper')}
            />
          </div>

          {dialogError && (
            <A.Alert alert="error" side="⚠️" mb="10">
              {dialogError}
            </A.Alert>
          )}

          <div className={styles.dialogActions}>
            <button
              type="button"
              className={`${styles.dialogBtn} ${styles.cancel}`}
              onClick={closeDialog}
              disabled={loading}
            >
              {t('offers:cancel')}
            </button>
            <button
              type="button"
              className={`${styles.dialogBtn} ${styles.primary}`}
              onClick={() => {
                void handleDialogConfirm();
              }}
              disabled={loading}
            >
              {loading
                ? t('offers:saving')
                : editingId
                ? t('offers:save')
                : t('offers:create')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </T.Container>
  );
}
